import { useTranslation } from "react-i18next";
import {
  Clock, Server, Cloud, GitBranch, Phone, Plug, CheckCircle, Shield, Building, UserCheck,
  TrendingUp, Users, Target, Zap, Eye, Activity, Lock, Briefcase, Package, Calendar,
  Smile, BadgeCheck, GraduationCap, Stethoscope, Landmark, ShieldCheck, Radar,
  Database, Bell, BarChart3, Network, Monitor, CloudOff, Layers,
  AlertTriangle, UserX, KeyRound, FileDown, Building2, Factory, Truck, HeartPulse,
  ShoppingCart, Scale, Brain, ShieldAlert, Bug, Upload, Search, Sword,
  FileText, Bot, Crosshair, Wrench, Smartphone,
  Award, FlaskConical, Timer, Boxes, Laptop, ShieldX, Fingerprint, Ban,
  Cpu, Layers3, Terminal, Globe2, History
} from "lucide-react";

// Feature icon mapping
const featureIcons = {
  // Skydial features
  onpremise: Server,
  cloud: Cloud,
  hybrid: GitBranch,
  branch: Building,
  sip: Phone,

  // Voice API features
  integration: Plug,
  scalable: TrendingUp,
  reliable: Shield,

  // RFID features
  supply: TrendingUp,
  asset: Package,
  event: Calendar,

  // Networking features
  bandwidth: Zap,
  latency: Activity,
  rural: Building,

  // AI Counting - Benefits
  traffic: TrendingUp,
  optimize: Target,
  marketing: Users,
  experience: Smile,
  efficiency: Zap,

  // AI Counting - Analytics
  age: UserCheck,
  gender: Users,
  employee: BadgeCheck,
  reid: Eye,
  accuracy: Target,

  // AI Counting - Behavioral
  dwell: Clock,
  flow: Activity,
  anonymous: Shield,
  realtime: Clock,

  // VMS - Main Features
  efficient: Zap,
  professional: Briefcase,
  compliance: Shield,
  secure: Lock,

  // VMS - Sectors
  corporate: Building,
  government: Landmark,
  hospital: Stethoscope,
  education: GraduationCap,

  // SOC - Main Features
  visibility: Eye,
  monitoring: Radar,
  ai: Brain,
  soccompliance: CheckCircle,
  sovereignty: CloudOff,
  multitenancy: Layers,

  // SOC - Architecture
  collection: Database,
  normalization: Layers,
  correlation: Zap,
  storage: Server,
  alerting: Bell,
  reporting: BarChart3,

  // SOC - Log Sources
  network: Network,
  os: Monitor,
  identity: KeyRound,
  soccloud: Cloud,
  endpoint: ShieldCheck,
  applications: Briefcase,

  // SOC - Use Cases
  abnormal: AlertTriangle,
  privilege: KeyRound,
  compromised: UserX,
  ransomware: ShieldAlert,
  insider: UserX,
  massdownload: FileDown,
  multisite: Building2,

  // SOC - Industries
  manufacturing: Factory,
  logistics: Truck,
  healthcare: HeartPulse,
  financial: Landmark,
  retail: ShoppingCart,
  socprofessional: Scale,

  // VAPT - Main Features
  autonomous: Bot,
  agentic: Boxes,
  onprem: Server,
  supervised: UserCheck,
  boxmodes: Lock,
  vaptreporting: FileText,

  // VAPT - Stages
  upload: Upload,
  assessment: Search,
  pentest: Sword,
  report: FileText,

  // VAPT - Agents
  orchestrator: Brain,
  recon: Radar,
  scanner: Crosshair,
  exploit: FlaskConical,
  vaptreporter: FileText,

  // VAPT - Coverage
  systemva: Monitor,
  mobile: Smartphone,
  api: Plug,
  server: Server,
  appvapt: Bug,
  ad: KeyRound,

  // VAPT - Testing Modes
  blackbox: Eye,
  greybox: Users,
  whitebox: ShieldCheck,

  // VAPT - Impact
  discovery: Search,
  turnaround: Timer,
  toolchain: Wrench,
  reporttime: Clock,

  // VAPT - Trust
  humanloop: UserCheck,
  precision: Target,
  pilot: Award,

  // FirstProtect - Main Features
  fpprevention: ShieldX,
  fpondevice: Brain,
  fpsigma: Layers3,
  fpcrossplatform: Laptop,
  fpuserspace: Cpu,
  fpmodules: Boxes,

  // FirstProtect - AgentProtect
  fpidentity: Fingerprint,
  fpenforcement: Ban,
  fpforensics: History,

  // FirstProtect - Protect Modules
  fpdata: Database,
  fpshell: Terminal,
  fpcredential: KeyRound,
  fpapp: Globe2,
  fproot: Server,
  fpdestructive: ShieldAlert,

  // FirstProtect - Deployment
  fpzerotouch: Upload,
  fpdataplane: Lock,
  fpauditmode: Eye,
  fpoffline: CloudOff,
  fpsoc: Radar,
  fpalertquality: CheckCircle,

  // FirstProtect - Proof
  fpexfil: ShieldCheck,
  fpofflineproof: CloudOff,
  fpearlier: Timer,

  // FirstProtect - Stats
  fplatency: Zap,
  fpefficacy: Target,
  fpcpu: Cpu,
  fpplatforms: Laptop,
};

const SolutionFeatures = ({ solution }) => {
  const { t } = useTranslation();

  const getFeatureIcon = (featureKey) => {
    return featureIcons[featureKey] || CheckCircle;
  };

  const renderStandardFeatures = () => (
    <>
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          Key Features
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
        {solution.features.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className={`w-10 h-10 ${solution.color}`} />
                </div>
                <div>
                  <p className="text-gray-700 text-base md:text-sm leading-relaxed">
                    {t(`solutions.${solution.key}.features.${feature}`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderVMSFeatures = () => (
    <>
      {/* Key Features Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          Key Features
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.features.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.features.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ideal For Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.sectors.title`)}
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
        {solution.features.sectors.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.features.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.features.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderFirstProtectFeatures = () => (
    <>
      {/* Stats Banner */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.stats.title`)}
        </h2>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-2 gap-6 mb-20">
        {solution.features.stats.map((feature, idx) => (
          <div
            key={idx}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-all duration-300"
          >
            <h3 className="text-3xl md:text-2xl font-bold text-indigo-600 mb-1">
              {t(`solutions.${solution.key}.stats.${feature}.title`)}
            </h3>
            <p className="text-gray-600 text-sm md:text-xs">
              {t(`solutions.${solution.key}.stats.${feature}.description`)}
            </p>
          </div>
        ))}
      </div>

      {/* Key Features Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          Key Features
        </h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.features.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.features.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* AgentProtect */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.agentprotect.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.agentprotect.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.agentprotect.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border-t-4 border-indigo-500"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                  <FeatureIcon className="w-7 h-7 text-indigo-600" />
                </div>
                <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs mb-1">
                  {t(`solutions.${solution.key}.agentprotect.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed">
                  {t(`solutions.${solution.key}.agentprotect.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Protect Modules */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.protectmodules.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.protectmodules.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.protectmodules.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <FeatureIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs">
                  {t(`solutions.${solution.key}.protectmodules.${feature}.title`)}
                </h3>
              </div>
              <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed pl-13">
                {t(`solutions.${solution.key}.protectmodules.${feature}.description`)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Deployment */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.deployment.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.deployment.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.deployment.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-textBlue" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.deployment.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.deployment.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Proof */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.proof.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-3xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.proof.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6">
        {solution.features.proof.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
            >
              <div className="flex flex-col items-center text-center">
                <FeatureIcon className="w-10 h-10 text-emerald-600 mb-3" />
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs mb-2">
                  {t(`solutions.${solution.key}.proof.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed italic">
                  {t(`solutions.${solution.key}.proof.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderVAPTFeatures = () => (
    <>
      {/* Key Features Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          Key Features
        </h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.features.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.features.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* How It Works - Stages */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.stages.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.stages.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-1 gap-6 mb-20">
        {solution.features.stages.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 group border border-gray-100"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-7 h-7 text-amber-600" />
                </div>
                <div className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs mb-1">
                  {t(`solutions.${solution.key}.stages.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed">
                  {t(`solutions.${solution.key}.stages.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Agent Swarm */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.agents.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.agents.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.agents.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <FeatureIcon className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs">
                  {t(`solutions.${solution.key}.agents.${feature}.title`)}
                </h3>
              </div>
              <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed pl-13">
                {t(`solutions.${solution.key}.agents.${feature}.description`)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Platform Capabilities */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.coverage.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.coverage.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.coverage.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-textBlue" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.coverage.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.coverage.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Testing Methodology */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.testingmodes.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.testingmodes.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.testingmodes.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border-t-4 border-amber-500"
            >
              <div className="flex flex-col items-center text-center">
                <FeatureIcon className="w-10 h-10 text-amber-600 mb-3" />
                <h3 className="font-semibold text-gray-800 text-base md:text-sm mb-2">
                  {t(`solutions.${solution.key}.testingmodes.${feature}.title`)}
                </h3>
                <p className="text-gray-600 text-sm md:text-xs leading-relaxed mb-3">
                  {t(`solutions.${solution.key}.testingmodes.${feature}.description`)}
                </p>
                <span className="inline-block bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                  {t(`solutions.${solution.key}.testingmodes.${feature}.knows`)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* The Measurable Difference */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.impact.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.impact.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-2 gap-6 mb-20">
        {solution.features.impact.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                <FeatureIcon className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm md:text-xs mb-1">
                {t(`solutions.${solution.key}.impact.${feature}.title`)}
              </h3>
              <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed">
                {t(`solutions.${solution.key}.impact.${feature}.description`)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Trust & Governance */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.trust.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-3xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.trust.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6">
        {solution.features.trust.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.trust.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.trust.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderSOCFeatures = () => (
    <>
      {/* Key Features Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          Key Features
        </h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.features.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.features.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* How It Works - Architecture */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.architecture.title`)}
        </h2>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm md:text-xs">
          {t(`solutions.${solution.key}.architecture.subtitle`)}
        </p>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.architecture.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 ease-in-out transform hover:-translate-y-1 group border border-gray-100"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs mb-1">
                  {t(`solutions.${solution.key}.architecture.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed">
                  {t(`solutions.${solution.key}.architecture.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Supported Log Sources */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.logsources.title`)}
        </h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6 mb-20">
        {solution.features.logsources.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center space-x-3 mb-2">
                <FeatureIcon className="w-6 h-6 text-textBlue flex-shrink-0" />
                <h3 className="font-semibold text-gray-800 text-sm md:text-xs">
                  {t(`solutions.${solution.key}.logsources.${feature}.title`)}
                </h3>
              </div>
              <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed pl-9">
                {t(`solutions.${solution.key}.logsources.${feature}.items`)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Common Security Use Cases */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.usecases.title`)}
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6 mb-20">
        {solution.features.usecases.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                    {t(`solutions.${solution.key}.usecases.${feature}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                    {t(`solutions.${solution.key}.usecases.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Industries We Protect */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.industries.title`)}
        </h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6">
        {solution.features.industries.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <FeatureIcon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm md:text-xs mb-1">
                    {t(`solutions.${solution.key}.industries.${feature}.title`)}
                  </h3>
                  <p className="text-gray-500 text-xs md:text-[11px] leading-relaxed">
                    {t(`solutions.${solution.key}.industries.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderAICountingFeatures = () => (
    <>
      {/* Key Benefits Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          Key Benefits
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6 mb-20">
        {solution.features.benefits.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-textBlue" />
                </div>
                <div>
                  <p className="text-gray-700 text-base md:text-sm leading-relaxed">
                    {t(`solutions.${solution.key}.features.${feature}`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Advanced Analytics Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.analytics`)}
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6 mb-20">
        {solution.features.analytics.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-purple-600" />
                </div>
                <div>
                  <p className="text-gray-700 text-base md:text-sm leading-relaxed">
                    {t(`solutions.${solution.key}.features.${feature}`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Behavioral Insights Section */}
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
          {t(`solutions.${solution.key}.behavioral`)}
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
        {solution.features.behavioral.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
            >
              <div className="flex items-start space-x-4">
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-10 h-10 text-indigo-600" />
                </div>
                <div>
                  <p className="text-gray-700 text-base md:text-sm leading-relaxed">
                    {t(`solutions.${solution.key}.features.${feature}`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderSkydialFeatures = () => {
    const benefitIcons = {
      costEffective: "/skydial-cost-effective.svg",
      flexible: "/skydial-flexible.svg",
      scalable: "/skydial-scalable.svg",
      updates: "/skydial-updates.svg",
      integration: "/skydial-integration.svg",
    };
    const featureIcons = {
      ivr: "/skydial-ivr.svg",
      voicemail: "/skydial-voicemail.svg",
      forwarding: "/skydial-forwarding.svg",
      conference: "/skydial-conference.svg",
      management: "/skydial-management.svg",
    };
    const whyIcons = {
      security: "/skydial-cyber-security.svg",
      easy: "/skydial-interactive.svg",
      modern: "/skydial-modernization.svg",
      support: "/skydial-customer-support.svg",
      cost: "/skydial-bonus.svg",
    };

    return (
      <>
        {/* PBX Solutions */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
            PBX Solutions
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-1 gap-6 mb-20">
          {solution.features.map((feature, idx) => {
            const FeatureIcon = getFeatureIcon(feature);
            return (
              <div
                key={idx}
                className="bg-blueLight p-6 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
              >
                <div className="flex items-start space-x-4">
                  <div className="transition-transform duration-300 group-hover:scale-110">
                    <FeatureIcon className="w-10 h-10 text-textBlue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-base md:text-sm">
                      {t(`solutions.${solution.key}.features.${feature}.title`)}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-xs leading-relaxed mt-1">
                      {t(`solutions.${solution.key}.features.${feature}.description`)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="text-center mb-10">
          <span className="inline-block bg-textBlue text-white text-md px-6 py-2 rounded-full mb-4">
            {t("solutions.skydial.benefits.label")}
          </span>
          <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-gray-800">
            {t("solutions.skydial.benefits.title")}
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
            {t("solutions.skydial.benefits.description")}
          </p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-20">
          {Object.keys(benefitIcons).map((key) => (
            <div
              key={key}
              className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition flex flex-col items-center text-center"
            >
              <img src={benefitIcons[key]} alt="" className="w-12 h-12 mb-3" />
              <p className="text-base font-medium text-gray-800">
                {t(`solutions.skydial.benefits.items.${key}`)}
              </p>
            </div>
          ))}
        </div>

        {/* Key Features */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-textBlue inline-block relative group">
            {t("solutions.skydial.keyfeatures.title")}
            <span className="block h-1 w-12 bg-textBlue mx-auto mt-2 transition-all duration-300 group-hover:w-24"></span>
          </h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-6 mb-20 justify-items-center">
          {Object.keys(featureIcons).map((key) => (
            <div
              key={key}
              className="bg-white p-6 rounded-lg shadow-md w-full max-w-xs flex flex-col items-center space-y-4 transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-blue-50"
            >
              <img src={featureIcons[key]} alt="" className="w-16 h-16" />
              <p className="text-base font-medium text-gray-800 text-center">
                {t(`solutions.skydial.keyfeatures.items.${key}`)}
              </p>
            </div>
          ))}
        </div>

        {/* Why Choose Skydial */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-2xl sm:text-xl font-bold text-textBlue inline-block relative group">
            {t("solutions.skydial.why.title")}
            <span className="block h-1 w-12 bg-textBlue mx-auto mt-2 transition-all duration-300 group-hover:w-24"></span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-2 sm:grid-cols-1 gap-12 items-center">
          <div className="grid grid-cols-1 sm:grid-cols-1 gap-6">
            {Object.keys(whyIcons).map((key) => (
              <div
                key={key}
                className="bg-blueLight p-5 rounded-xl shadow transition-all duration-300 ease-in-out transform hover:shadow-lg hover:-translate-y-1 hover:bg-white group"
              >
                <div className="flex items-center space-x-4">
                  <div className="transition-transform duration-300 group-hover:scale-110">
                    <img src={whyIcons[key]} alt="" className="w-12 h-12" />
                  </div>
                  <h3 className="text-lg sm:text-md font-semibold text-textBlueDark transition-colors duration-300 group-hover:text-textBlue">
                    {t(`solutions.skydial.why.items.${key}`)}
                  </h3>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center sm:hidden">
            <img
              src="/img-skydial-why.svg"
              alt="Why Choose Skydial"
              className="max-w-full h-auto transition duration-500 hover:scale-105"
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <section className="py-8 pb-24 px-6 md:px-16 sm:px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {solution.isSkydial ? (
          renderSkydialFeatures()
        ) : solution.isSOC ? (
          renderSOCFeatures()
        ) : solution.isVAPT ? (
          renderVAPTFeatures()
        ) : solution.isFirstProtect ? (
          renderFirstProtectFeatures()
        ) : solution.hasCategories ? (
          solution.hasSectors ? (
            renderVMSFeatures()
          ) : (
            renderAICountingFeatures()
          )
        ) : (
          renderStandardFeatures()
        )}
      </div>
    </section>
  );
};

export default SolutionFeatures;
